import React, { useEffect, useState, ReactNode } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { Location } from "history";
import { useTranslation } from "react-i18next";

import useDialog from "../../hooks/useDialog";
import { Button, Dialog } from "..";

import styles from "./StayOnPagePrompt.module.scss";

interface Props {
  when: boolean;
  children: ReactNode;
  title?: ReactNode;
  text?: ReactNode;
}

const StayOnPagePrompt = ({ when, children, title, text }: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    if (!isDialogActive && when) {
      setLastLocation(nextLocation);
      openDialog();
      return false;
    }
    return true;
  };

  useEffect(() => {
    window.onbeforeunload = when ? () => true : null;

    return () => {
      window.onbeforeunload = null;
    };
  }, [when]);

  const onStay = () => {
    closeDialog();
  };

  const onBack = () => {
    closeDialog();
    history.push(lastLocation!.pathname);
  };

  const promptActions = [
    <Button
      key="1"
      id="prompt-cancel-btn"
      variant="filled"
      onClick={onBack}
      className={styles.promptBack}
    >
      {t("prompt.back")}
    </Button>,
    <Button key="2" onClick={onStay} variant="outlined" id="prompt-back-btn">
      {t("prompt.stay")}
    </Button>,
  ];

  return (
    <>
      <Prompt
        when={when}
        message={(location) => handleBlockedNavigation(location as Location)}
      />
      {isDialogRendered && (
        <Dialog
          title={title || t("prompt.unsavedChangesTitle")}
          isActive={isDialogActive}
          actions={promptActions}
          onClose={onStay}
        >
          <span>{text || t("prompt.unsavedChangesMessage")}</span>
        </Dialog>
      )}
      {children}
    </>
  );
};

export default StayOnPagePrompt;
